import React, { useState, useEffect } from 'react';

import { Container, Row, Col, Card, Table, Badge, Form, Button, Spinner } from 'react-bootstrap';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import Select from 'react-select';

import { CUT_DRY_IMPORT_RUNS } from 'lib/networking/endpoints';
import { CUT_DRY_SYNC_STATUS_OPTIONS } from 'lib/enums/CutDrySyncStatus';
import { DATE_FORMAT_WITH_TIME } from 'lib/constants';
import { ENTITY_TYPE_OPTIONS } from 'lib/enums/EntityType';
import PaginationComponent from 'components/shared/PaginationComponent';
import useGet from 'lib/hooks/useGet';

const CutDryImportTracking = () => {
  const [currentPage, setCurrentPage] = useState(1); // PaginationComponent uses 1-based indexing
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({
    import_type: '',
    status: '',
    start_date: '',
  });

  const {
    data: { import_runs: importRuns = [], total_count: total = 0 } = {},
    loading,
    error,
    refetch,
  } = useGet(CUT_DRY_IMPORT_RUNS, {
    offset: (currentPage - 1) * pageSize,
    limit: pageSize,
    ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')),
  });

  // Trigger refetch when filters, page, or page size change
  useEffect(() => {
    refetch();
  }, [currentPage, pageSize, filters, refetch]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const getStatusBadge = status => {
    const statusColors = {
      COMPLETED: 'success',
      FAILED: 'danger',
      IN_PROGRESS: 'warning',
      PENDING: 'secondary',
    };
    return <Badge variant={statusColors[status] || 'secondary'}>{status}</Badge>;
  };

  return (
    <Container fluid>
      <div className="page-header">
        <h3 className="font-weight-bold">Cut Dry Import Tracking</h3>
      </div>

      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Header>
              <h5>Filters</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Import Type</Form.Label>
                    <Select
                      value={ENTITY_TYPE_OPTIONS.find(
                        option => option.value === filters.import_type,
                      )}
                      options={ENTITY_TYPE_OPTIONS}
                      onChange={e => handleFilterChange('import_type', e?.value || '')}
                      isSearchable
                      isClearable
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Select
                      value={CUT_DRY_SYNC_STATUS_OPTIONS.find(
                        option => option.value === filters.status,
                      )}
                      options={CUT_DRY_SYNC_STATUS_OPTIONS}
                      onChange={e => handleFilterChange('status', e?.value || '')}
                      isSearchable
                      isClearable
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={filters.start_date}
                      onChange={e => handleFilterChange('start_date', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col>
                  <Button
                    variant="primary"
                    onClick={() => {
                      setFilters({
                        import_type: '',
                        status: '',
                        start_date: '',
                      });
                      setCurrentPage(1);
                    }}
                  >
                    Clear Filters
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5>{total > 0 ? `${total} Import Runs` : 'Import Runs'}</h5>
              <PaginationComponent
                totalItems={total}
                itemsPerPage={pageSize}
                setItemsPerPage={setPageSize}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                disabled={loading}
              />
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center">
                  <Spinner animation="border" />
                </div>
              ) : error ? (
                <div className="text-center text-danger">
                  Error loading import runs: {error.message}
                </div>
              ) : (
                <>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Import Type</th>
                        <th>Status</th>
                        <th>Started</th>
                        <th>Processed</th>
                        <th>New Products Created</th>
                        <th>Updated Products</th>
                        <th>Rejected</th>
                        <th>Source File</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importRuns.length > 0 ? (
                        importRuns.map(run => (
                          <tr key={run.id}>
                            <td>{run.id}</td>
                            <td>
                              <Badge variant="info">{run.import_type}</Badge>
                            </td>
                            <td>{getStatusBadge(run.status)}</td>
                            <td>{dayjs(run.date_created).format(DATE_FORMAT_WITH_TIME)}</td>
                            <td>{run.total_rows_processed || 0}</td>
                            <td>{run.new_products_created || 0}</td>
                            <td>{run.existing_products_updated || 0}</td>
                            <td>
                              {run.rejected_rows > 0 ? (
                                <Badge variant="warning">{run.rejected_rows}</Badge>
                              ) : (
                                0
                              )}
                            </td>
                            <td>
                              {run.source_file_presigned_url ? (
                                <a
                                  href={run.source_file_presigned_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  Download
                                </a>
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td>
                              <Link
                                to={`/cut-dry-import-tracking/${run.id}`}
                                className="btn btn-sm btn-outline-primary"
                              >
                                View Details
                              </Link>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="11" className="text-center">
                            No import runs found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CutDryImportTracking;
